import React from 'react';
import { ResponsiveContainer, LineChart, Line, CartesianGrid, Tooltip, XAxis, YAxis, Legend } from 'recharts';

export type ProjectScurveRow = {
    weekLabel: string; // W1..Wn
    weekOf: string; // YYYY-MM-DD
    pvInc: number;
    pvCum: number;

    pvForecastInc?: number | null;
    pvForecastCum?: number | null;

    evInc: number;
    evCum: number;
    spi: number | null;
};

function fmtPct(v: any) {
    const n = Number(v);
    if (!Number.isFinite(n)) return '0%';
    return `${n.toFixed(1)}%`;
}

export default function ScurveChart({
    rows,
    height = 280,
    showIncrementInTooltip = false,
    wbsListVersion,
}: {
    rows: ProjectScurveRow[];
    height?: number;
    showIncrementInTooltip?: boolean;
    wbsListVersion?: number;
}) {
    const xTickFormatter = (value: string, index: number) => {
        if (!rows || rows.length === 0) return value;

        const lastIndex = rows.length - 1;

        // 처음과 마지막은 항상 표시
        if (index === 0 || index === lastIndex) {
            return value;
        }

        // 중간은 듬성듬성 (대략 6~8개만 보이게)
        const step = Math.ceil(rows.length / 8);
        return index % step === 0 ? value : '';
    };

    return (
        <div style={{ width: '100%', height }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rows} margin={{ left: 12, right: 12, top: 8, bottom: 0 }}>
                    {/* <CartesianGrid strokeDasharray="3 3" /> */}
                    <CartesianGrid strokeDasharray="3 6" vertical={false} />

                    <XAxis dataKey="weekLabel" tickMargin={8} />
                    <XAxis
                        dataKey="weekLabel"
                        tickFormatter={xTickFormatter}
                        tickMargin={8}
                        interval={0}
                        minTickGap={20}
                        tick={{ fontSize: 12 }}
                    />
                    {/* <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={42} /> */}
                    {/* 누적(0~100) */}
                    <YAxis
                        yAxisId="cum"
                        domain={[0, 100]}
                        ticks={[0, 25, 50, 75, 100]}
                        allowDecimals={false}
                        tickFormatter={(v) => `${v}%`}
                        width={42}
                        tick={{ fontSize: 12 }}
                    />

                    {/* 증가(우측) - 자동 스케일 */}
                    <YAxis
                        yAxisId="inc"
                        orientation="right"
                        // domain={[0, 'dataMax + 1']}
                        domain={[0, 15]} // 15%
                        tickCount={4}
                        tickFormatter={(v) => `${v}%`}
                        width={42}
                        tick={{ fontSize: 12 }}
                    />
                    <Legend
                        verticalAlign="bottom"
                        align="left"
                        height={18}
                        wrapperStyle={{ fontSize: 12, paddingLeft: 8 }}
                    />
                    <Tooltip
                        formatter={(value: any, name: any, ctx: any) => {
                            if (name === 'pvCum') return [fmtPct(value), '계획(누적)'];
                            if (name === 'evCum') return [fmtPct(value), '실적(누적)'];
                            if (name === 'pvForecastCum') return [fmtPct(value), '예측(누적)'];
                            if (name === 'pvInc') return [fmtPct(value), '계획(증가)'];
                            if (name === 'evInc') return [fmtPct(value), '실적(증가)'];
                            return [fmtPct(value), name];
                        }}
                        labelFormatter={(_, payload) => {
                            const r = payload?.[0]?.payload as ProjectScurveRow | undefined;
                            return r ? `${r.weekLabel} · ${r.weekOf}` : '';
                        }}
                    />

                    {/* 누적 */}
                    {/* PV 누적 */}
                    <Line
                        yAxisId="cum"
                        type="monotone"
                        dataKey="pvCum"
                        name="계획(PV)"
                        dot={false}
                        activeDot={{ r: 4 }}
                        stroke="#2563eb"
                        strokeWidth={3}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />

                    {/* ✅ PV Forecast 누적 */}
                    <Line
                        yAxisId="cum"
                        type="monotone"
                        dataKey="pvForecastCum"
                        name="예측(Forecast)"
                        dot={false}
                        activeDot={{ r: 4 }}
                        stroke="#f97316"
                        strokeWidth={2.5}
                        strokeDasharray="10 6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        // connectNulls
                    />

                    {/* 확정 EV 누적 */}
                    <Line
                        yAxisId="cum"
                        type="monotone"
                        dataKey="evCum"
                        name="확정(EV)"
                        dot={false}
                        activeDot={{ r: 4 }}
                        stroke="#f97316"
                        strokeWidth={3}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    {/* <Line yAxisId="cum" type="monotone" dataKey="pvCum" dot={false} strokeWidth={2.6} />
                    <Line yAxisId="cum" type="monotone" dataKey="evCum" dot={false} strokeWidth={2.6} /> */}

                    {/* 필요 시 증가도 표시(툴팁만으로 충분하면 라인은 숨겨도 됨) */}
                    {/* {showIncrementInTooltip ? (
                        <>
                            <Line
                                yAxisId="inc"
                                type="monotone"
                                dataKey="pvInc"
                                name="계획 증가"
                                dot={false}
                                stroke="#2563eb"
                                strokeDasharray="3 6"
                                strokeWidth={1.6}
                                strokeOpacity={0.35}
                            />
                            <Line
                                yAxisId="inc"
                                type="monotone"
                                dataKey="evInc"
                                name="확정 증가"
                                dot={false}
                                stroke="#16a34a"
                                strokeDasharray="3 6"
                                strokeWidth={1.6}
                                strokeOpacity={0.35}
                            />
                        </>
                    ) : null} */}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}
